import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { 
  ArrowLeftIcon, 
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  SpeakerWaveIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

// Normalize heading deviation to -180 to +180 range
const normalizeHeadingDev = (dev) => {
  let d = dev % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
};

// Deviation Chart Component - High quality canvas chart
const DeviationChart = ({ chartData, height = 300 }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !chartData || chartData.labels.length === 0) return;
    
    // Get actual container size for responsive canvas
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = rect.width;
    const canvasHeight = height;
    
    // Set canvas size with device pixel ratio for crisp rendering
    canvas.width = width * dpr;
    canvas.height = canvasHeight * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${canvasHeight}px`;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    
    const padding = { top: 35, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = canvasHeight - padding.top - padding.bottom;
    
    // Clear canvas with dark background
    ctx.fillStyle = '#111827'; // gray-900
    ctx.fillRect(0, 0, width, canvasHeight);
    
    // Y-axis scales
    const scales = {
      speed: { min: -30, max: 30, color: '#10b981' },      // emerald-500
      altitude: { min: -250, max: 250, color: '#f43f5e' }, // rose-500
      heading: { min: -90, max: 90, color: '#6366f1' }     // indigo-500
    };
    
    // Draw grid lines
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartHeight / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }
    
    // Center line (zero) - highlighted
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 1.5;
    const centerY = padding.top + chartHeight / 2;
    ctx.beginPath();
    ctx.moveTo(padding.left, centerY);
    ctx.lineTo(width - padding.right, centerY);
    ctx.stroke();
    
    // Y-axis labels
    ctx.fillStyle = '#9ca3af';
    ctx.font = '12px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const yLabels = ['+100%', '+50%', '0', '-50%', '-100%'];
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartHeight / 4) * i;
      ctx.fillText(yLabels[i], padding.left - 8, y);
    }
    
    // X-axis labels
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const maxTime = parseFloat(chartData.labels[chartData.labels.length - 1]) || 1;
    const xSteps = Math.min(6, Math.ceil(maxTime / 10));
    for (let i = 0; i <= xSteps; i++) {
      const x = padding.left + (chartWidth / xSteps) * i;
      const time = (maxTime / xSteps) * i;
      ctx.fillText(`${time.toFixed(0)}s`, x, canvasHeight - padding.bottom + 8);
    }
    
    // Draw lines with better quality
    const drawLine = (data, scale, color) => {
      if (!data || data.length < 1) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = padding.left + (data.length > 1 ? (i / (data.length - 1)) * chartWidth : chartWidth / 2);
        const clampedValue = Math.max(scale.min, Math.min(scale.max, data[i]));
        const normalized = (clampedValue - scale.min) / (scale.max - scale.min);
        const y = padding.top + chartHeight - (normalized * chartHeight);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };
    
    drawLine(chartData.speed, scales.speed, scales.speed.color);
    drawLine(chartData.altitude, scales.altitude, scales.altitude.color);
    drawLine(chartData.heading, scales.heading, scales.heading.color);
    
    // Draw vertical dashed lines at task boundaries (for real test mode)
    if (chartData.taskBoundaries && chartData.taskBoundaries.length > 0) {
      ctx.strokeStyle = '#9ca3af'; // gray-400
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]); // Dashed line pattern
      
      chartData.taskBoundaries.forEach((boundaryTime, idx) => {
        // Calculate x position based on time
        const x = padding.left + (boundaryTime / maxTime) * chartWidth;
        ctx.beginPath();
        ctx.moveTo(x, padding.top);
        ctx.lineTo(x, padding.top + chartHeight);
        ctx.stroke();
        
        // Add task number label
        ctx.fillStyle = '#6b7280';
        ctx.font = '10px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`T${idx + 1}`, x - 20, padding.top + chartHeight + 22);
      });
      
      // Add last task label
      ctx.fillText(`T${chartData.taskBoundaries.length + 1}`, width - padding.right - 20, padding.top + chartHeight + 22);
      
      ctx.setLineDash([]); // Reset to solid line
    }
    
    // Legend at top
    const legendItems = [
      { color: '#10b981', label: 'Speed' },
      { color: '#f43f5e', label: 'Altitude' },
      { color: '#6366f1', label: 'Heading' }
    ];
    ctx.font = '12px system-ui, sans-serif';
    let legendX = padding.left;
    legendItems.forEach(item => {
      ctx.strokeStyle = item.color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(legendX, 16);
      ctx.lineTo(legendX + 24, 16);
      ctx.stroke();
      ctx.fillStyle = '#e5e7eb';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.label, legendX + 30, 16);
      legendX += ctx.measureText(item.label).width + 60;
    });
    
  }, [chartData, height]);
  
  return (
    <div ref={containerRef} className="w-full h-full bg-gray-900 rounded-lg">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

const MICResults = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const { results, deviationData, taskResults, isRealTest } = location.state || {};
  
  const [subject, setSubject] = useState(null);
  const [bannerImageLoaded, setBannerImageLoaded] = useState(false);

  // Fetch subject data for banner image
  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const response = await apiClient.get(`/courses/subjects/${subjectId}/`);
        setSubject(response.data);
      } catch (error) {
        // Failed to fetch subject
      }
    };
    if (subjectId) {
      fetchSubject();
    }
  }, [subjectId]);

  // Calculate performance grade
  const getGrade = (score) => {
    if (score >= 90) return { grade: 'A', color: 'text-green-400', bg: 'bg-green-500', passed: true };
    if (score >= 80) return { grade: 'B', color: 'text-blue-400', bg: 'bg-blue-500', passed: true };
    if (score >= 70) return { grade: 'C', color: 'text-yellow-400', bg: 'bg-yellow-500', passed: true };
    if (score >= 60) return { grade: 'D', color: 'text-orange-400', bg: 'bg-orange-500', passed: false };
    return { grade: 'F', color: 'text-red-400', bg: 'bg-red-500', passed: false };
  };

  // For real test mode, we'll recalculate the final score using the new logic
  // This is done in realTestAverages useMemo below
  const rawFinalScore = parseFloat(results?.final_score) || 0;

  // Calculate average instrument scores for real test mode
  // New logic: compass sum/10.2, altimeter sum/9, airspeed sum/6, audio correct/total occurrences
  const realTestAverages = useMemo(() => {
    if (!isRealTest || !taskResults || taskResults.length === 0) return null;
    
    // Sum all raw scores (not averages)
    let compassSum = 0;
    let altimeterSum = 0;
    let airspeedSum = 0;
    let auditoryCorrectTotal = 0;
    let auditoryExpectedTotal = 0;
    
    taskResults.forEach(task => {
      // Add raw scores from each task
      compassSum += task.headingScore || 0;
      altimeterSum += task.altitudeScore || 0;
      airspeedSum += task.speedScore || 0;
      
      // Track total auditory correct/expected across all tasks
      if (task.auditoryResults) {
        auditoryCorrectTotal += task.auditoryResults.correct || 0;
      }
      if (task.auditoryStats) {
        auditoryExpectedTotal += task.auditoryStats.totalExpected || 0;
      }
    });
    
    // Calculate percentages with specified divisors
    // Compass: sum / 10.2 (max possible is 1020 from 12 tasks with varying weights)
    // Altimeter: sum / 9 (max possible is 900)
    // Airspeed: sum / 6 (max possible is 600 from 6 tasks with airspeed)
    // Audio: correct / total occurrences * 100
    const compassPercent = Math.min(100, compassSum / 10.2);
    const altimeterPercent = Math.min(100, altimeterSum / 9);
    const airspeedPercent = Math.min(100, airspeedSum / 6);
    const auditoryPercent = auditoryExpectedTotal > 0 
      ? Math.min(100, (auditoryCorrectTotal / auditoryExpectedTotal) * 100) 
      : 0;
    
    // Final score is average of the 4 percentages
    const calculatedFinalScore = (compassPercent + altimeterPercent + airspeedPercent + auditoryPercent) / 4;
    
    return {
      heading: compassPercent,
      altitude: altimeterPercent,
      speed: airspeedPercent,
      auditory: auditoryPercent,
      auditoryCorrect: auditoryCorrectTotal,
      auditoryExpected: auditoryExpectedTotal,
      finalScore: calculatedFinalScore
    };
  }, [isRealTest, taskResults]);

  // Use recalculated final score for real test mode, otherwise use the stored score
  const finalScore = isRealTest && realTestAverages ? realTestAverages.finalScore : rawFinalScore;
  const gradeInfo = getGrade(finalScore);

  // Combine all deviation data from all tasks for real test mode
  // Each task's deviation starts from 0 (reset between tasks)
  // Track task boundaries for vertical dashed lines
  const { combinedDeviationData, taskBoundaries } = useMemo(() => {
    if (isRealTest && taskResults && taskResults.length > 0) {
      let combined = [];
      let timeOffset = 0;
      const boundaries = []; // Store time positions where tasks end
      
      taskResults.forEach((task, taskIndex) => {
        const taskDuration = 75; // Each task is 75 seconds
        
        if (task.deviationData && Array.isArray(task.deviationData) && task.deviationData.length > 0) {
          // Add each task's deviation data with adjusted time
          // The deviation data time is relative to task start (0 to ~75s)
          const taskData = task.deviationData.map(d => ({
            time: (d.time || 0) + timeOffset,
            headingDeviation: d.headingDeviation || 0,
            altitudeDeviation: d.altitudeDeviation || 0,
            speedDeviation: d.speedDeviation || 0,
            taskIndex: taskIndex + 1
          }));
          combined = [...combined, ...taskData];
        }
        
        // Add task boundary (end of this task)
        timeOffset += taskDuration;
        if (taskIndex < taskResults.length - 1) {
          boundaries.push(timeOffset); // Don't add boundary after last task
        }
      });
      
      return { combinedDeviationData: combined, taskBoundaries: boundaries };
    }
    return { combinedDeviationData: deviationData || [], taskBoundaries: [] };
  }, [isRealTest, taskResults, deviationData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const data = combinedDeviationData;
    if (!data || data.length === 0) return null;
    return {
      labels: data.map(d => d.time.toFixed(0)),
      heading: data.map(d => normalizeHeadingDev(d.headingDeviation)),
      altitude: data.map(d => d.altitudeDeviation),
      speed: data.map(d => d.speedDeviation),
      taskBoundaries: taskBoundaries // Pass task boundaries for vertical lines
    };
  }, [combinedDeviationData, taskBoundaries]);

  if (!results) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">No Results Found</h2>
          <p className="text-gray-400 mb-6">The results could not be found.</p>
          <button
            onClick={() => navigate(`${gamesBase}/mic/${subjectId}`)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Setup
          </button>
        </div>
      </div>
    );
  }

  // Calculate auditory score display
  const auditoryTotal = results.auditory_total_expected || ((results.auditory_correct || 0) + (results.auditory_missed || 0));
  const auditoryScorePercent = results.auditory_score !== undefined ? parseFloat(results.auditory_score) : 
    (auditoryTotal > 0 ? ((results.auditory_correct || 0) / auditoryTotal) * 100 : 0);

  // Get banner image URL - use banner_image first, fall back to image
  const getBannerImageUrl = () => {
    const imageSource = subject?.banner_image || subject?.image;
    if (!imageSource) return null;
    // Use production URL for deployed app
    const apiUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:8000' 
      : 'https://api.air-brief.com';
    return imageSource.startsWith('http') 
      ? imageSource 
      : `${apiUrl}${imageSource}`;
  };

  const bannerImageUrl = getBannerImageUrl();

  return (
    <div className="h-full bg-gray-900 flex flex-col">
      {/* Banner with background image and fade effects */}
      <div className="relative h-28 bg-black overflow-hidden flex-shrink-0">
        {/* Background Image */}
        {bannerImageUrl && (
          <>
            <img
              src={bannerImageUrl}
              alt="MIC Results"
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out transform ${
                bannerImageLoaded 
                  ? 'opacity-100 scale-105' 
                  : 'opacity-60 scale-110'
              }`}
              style={{ objectPosition: 'center center' }}
              onLoad={() => setBannerImageLoaded(true)}
              onError={() => setBannerImageLoaded(false)}
            />
            {/* Extended black fade on sides */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, black 0%, transparent 15%, transparent 60%, black 75%)'
            }}></div>
            {/* Subtle top and bottom fade */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30"></div>
          </>
        )}
        {/* Fallback gradient if no image */}
        {!bannerImageUrl && (
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700"></div>
        )}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/20"></div>
        
        {/* Content */}
        <div className="relative z-10 h-full max-w-5xl mx-auto px-6 flex items-center justify-between">
          {/* Left side - Title and mode */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`${gamesBase}/mic/${subjectId}`)}
              className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">MIC Results</h1>
              <p className="text-sm text-white/80">
                {results.run_mode === 'predefined' ? `Task ${results.predefined_task_number}` : 
                 results.run_mode === 'custom' ? 'Custom Session' : 'Real Test Mode'}
                {' • '}{Math.floor(results.duration_seconds / 60)}:{(results.duration_seconds % 60).toString().padStart(2, '0')}
              </p>
            </div>
          </div>
          
          {/* Right side - Grade */}
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="text-4xl font-bold text-white">{finalScore.toFixed(1)}%</div>
              <div className="text-sm text-white/80">Overall Score</div>
            </div>
            <div className={`w-16 h-16 rounded-full ${gradeInfo.bg} flex items-center justify-center shadow-lg`}>
              <span className="text-3xl font-bold text-white">{gradeInfo.grade}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-5xl mx-auto flex flex-col gap-4">
          {/* Performance Scores Row */}
          <div className="grid grid-cols-4 gap-4">
            {/* Compass/Heading */}
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400 mb-2">Compass</div>
              <div className={`text-3xl font-bold ${
                isRealTest 
                  ? (realTestAverages?.heading >= 70 ? 'text-emerald-400' : 'text-red-400')
                  : (results.compass_mode === 'off' ? 'text-gray-500' :
                     parseFloat(results.heading_score || 0) >= 70 ? 'text-emerald-400' : 'text-red-400')
              }`}>
                {isRealTest 
                  ? `${(realTestAverages?.heading || 0).toFixed(0)}%`
                  : `${parseFloat(results.heading_score || 0).toFixed(0)}%`}
              </div>
              {!isRealTest && results.compass_mode === 'constant' && (
                <div className="text-xs text-gray-500 mt-1">×0.8 (constant)</div>
              )}
              {isRealTest && (
                <div className="text-xs text-gray-500 mt-1">avg of {taskResults?.length || 0} runs</div>
              )}
            </div>
            
            {/* Altimeter */}
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400 mb-2">Altimeter</div>
              <div className={`text-3xl font-bold ${
                isRealTest 
                  ? (realTestAverages?.altitude >= 70 ? 'text-emerald-400' : 'text-red-400')
                  : (results.altimeter_mode === 'off' ? 'text-gray-500' :
                     parseFloat(results.altitude_score || 0) >= 70 ? 'text-emerald-400' : 'text-red-400')
              }`}>
                {isRealTest 
                  ? `${(realTestAverages?.altitude || 0).toFixed(0)}%`
                  : `${parseFloat(results.altitude_score || 0).toFixed(0)}%`}
              </div>
              {!isRealTest && results.altimeter_mode === 'constant' && (
                <div className="text-xs text-gray-500 mt-1">×0.8 (constant)</div>
              )}
              {isRealTest && (
                <div className="text-xs text-gray-500 mt-1">avg of {taskResults?.length || 0} runs</div>
              )}
            </div>
            
            {/* Airspeed */}
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400 mb-2">Airspeed</div>
              <div className={`text-3xl font-bold ${
                isRealTest 
                  ? (realTestAverages?.speed >= 70 ? 'text-emerald-400' : 'text-red-400')
                  : (!results.airspeed_enabled ? 'text-gray-500' :
                     parseFloat(results.speed_score || 0) >= 70 ? 'text-emerald-400' : 'text-red-400')
              }`}>
                {isRealTest 
                  ? `${(realTestAverages?.speed || 0).toFixed(0)}%`
                  : `${parseFloat(results.speed_score || 0).toFixed(0)}%`}
              </div>
              {isRealTest && (
                <div className="text-xs text-gray-500 mt-1">avg of {taskResults?.length || 0} runs</div>
              )}
            </div>
            
            {/* Auditory Task */}
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400 mb-2">Auditory Task</div>
              <div className={`text-3xl font-bold ${
                isRealTest 
                  ? (realTestAverages?.auditory >= 70 ? 'text-emerald-400' : 'text-red-400')
                  : (results.auditory_task_enabled ? (auditoryScorePercent >= 70 ? 'text-emerald-400' : 'text-red-400') : 'text-gray-500')
              }`}>
                {isRealTest 
                  ? `${(realTestAverages?.auditory || 0).toFixed(0)}%`
                  : (results.auditory_task_enabled ? `${auditoryScorePercent.toFixed(0)}%` : '0%')}
              </div>
              {!isRealTest && results.auditory_task_enabled && (
                <div className="text-xs text-gray-500 mt-1">{results.auditory_correct}/{auditoryTotal}</div>
              )}
              {isRealTest && (
                <>
                  <div className="text-xs text-gray-500 mt-1">
                    {realTestAverages?.auditoryCorrect || 0}/{realTestAverages?.auditoryExpected || 0} solved
                  </div>
                  <div className="text-xs text-gray-500">avg of {taskResults?.length || 0} runs</div>
                </>
              )}
            </div>
          </div>

          {/* Session Settings Row - hide for real test mode */}
          {!isRealTest && (
            <div className="bg-gray-800 rounded-xl px-5 py-3 flex items-center gap-4">
              <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 rounded-lg text-sm bg-gray-700 text-gray-300">
                  Compass: {results.compass_mode}
                </span>
                <span className="px-3 py-1 rounded-lg text-sm bg-gray-700 text-gray-300">
                  Altimeter: {results.altimeter_mode}
                </span>
                <span className={`px-3 py-1 rounded-lg text-sm ${results.airspeed_enabled ? 'bg-emerald-900/50 text-emerald-400' : 'bg-gray-700 text-gray-500'}`}>
                  Airspeed {results.airspeed_enabled ? '✓' : '✗'}
                </span>
                <span className={`px-3 py-1 rounded-lg text-sm ${results.turbulence_enabled ? 'bg-emerald-900/50 text-emerald-400' : 'bg-gray-700 text-gray-500'}`}>
                  Turbulence {results.turbulence_enabled ? '✓' : '✗'}
                </span>
                <span className={`px-3 py-1 rounded-lg text-sm ${results.auditory_task_enabled ? 'bg-emerald-900/50 text-emerald-400' : 'bg-gray-700 text-gray-500'}`}>
                  Audio {results.auditory_task_enabled ? '✓' : '✗'}
                </span>
              </div>
            </div>
          )}
          
          {/* Deviation Chart - Show above task table for real test mode */}
          {isRealTest && (
            <div className="bg-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Deviation Over Time (All 12 Tasks Combined)
              </h3>
              {chartData ? (
                <DeviationChart chartData={chartData} height={300} />
              ) : (
                <div className="h-[250px] flex items-center justify-center">
                  <div className="text-gray-500">No deviation data available</div>
                </div>
              )}
            </div>
          )}

          {/* Real Test Mode - Detailed Task Scores Table */}
          {isRealTest && taskResults && taskResults.length > 0 && (
            <div className="bg-gray-800 rounded-xl p-4 overflow-x-auto">
              <div className="text-sm text-gray-400 mb-3">Task Scores ({taskResults.length} tasks)</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left py-2 px-2">Task</th>
                    <th className="text-center py-2 px-2">Compass</th>
                    <th className="text-center py-2 px-2">Altimeter</th>
                    <th className="text-center py-2 px-2">Airspeed</th>
                    <th className="text-center py-2 px-2">Audio</th>
                    <th className="text-center py-2 px-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {taskResults.map((task, idx) => {
                    // Get task config from PREDEFINED_TASKS equivalent
                    const taskConfigs = [
                      { compass: 'constant', altimeter: 'off', airspeed: false, audio: false },
                      { compass: 'off', altimeter: 'constant', airspeed: false, audio: false },
                      { compass: 'constant', altimeter: 'constant', airspeed: false, audio: false },
                      { compass: 'changing', altimeter: 'off', airspeed: false, audio: false },
                      { compass: 'changing', altimeter: 'constant', airspeed: false, audio: false },
                      { compass: 'changing', altimeter: 'changing', airspeed: false, audio: false },
                      { compass: 'constant', altimeter: 'changing', airspeed: true, audio: false },
                      { compass: 'changing', altimeter: 'constant', airspeed: true, audio: false },
                      { compass: 'changing', altimeter: 'changing', airspeed: true, audio: false },
                      { compass: 'constant', altimeter: 'changing', airspeed: true, audio: true },
                      { compass: 'changing', altimeter: 'constant', airspeed: true, audio: true },
                      { compass: 'changing', altimeter: 'changing', airspeed: true, audio: true },
                    ];
                    const config = taskConfigs[idx] || {};
                    
                    const getScoreDisplay = (score, mode, isEnabled = true) => {
                      if (mode === 'off' || !isEnabled) return <span className="text-gray-500">OFF</span>;
                      if (mode === 'constant') return <span className="text-yellow-400">{score?.toFixed(0)}%</span>;
                      return <span className={score >= 70 ? 'text-emerald-400' : 'text-red-400'}>{score?.toFixed(0)}%</span>;
                    };
                    
                    return (
                      <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 px-2 text-gray-300">T{idx + 1}</td>
                        <td className="text-center py-2 px-2">
                          {config.compass === 'off' 
                            ? <span className="text-gray-500">OFF</span>
                            : config.compass === 'constant'
                              ? <span className="text-yellow-400">{task.headingScore?.toFixed(0)}%</span>
                              : <span className={task.headingScore >= 70 ? 'text-emerald-400' : 'text-red-400'}>{task.headingScore?.toFixed(0)}%</span>
                          }
                        </td>
                        <td className="text-center py-2 px-2">
                          {config.altimeter === 'off' 
                            ? <span className="text-gray-500">OFF</span>
                            : config.altimeter === 'constant'
                              ? <span className="text-yellow-400">{task.altitudeScore?.toFixed(0)}%</span>
                              : <span className={task.altitudeScore >= 70 ? 'text-emerald-400' : 'text-red-400'}>{task.altitudeScore?.toFixed(0)}%</span>
                          }
                        </td>
                        <td className="text-center py-2 px-2">
                          {!config.airspeed 
                            ? <span className="text-gray-500">OFF</span>
                            : <span className={task.speedScore >= 70 ? 'text-emerald-400' : 'text-red-400'}>{task.speedScore?.toFixed(0)}%</span>
                          }
                        </td>
                        <td className="text-center py-2 px-2">
                          {!config.audio 
                            ? <span className="text-gray-500">OFF</span>
                            : (
                              <span className={task.auditoryScore >= 70 ? 'text-emerald-400' : 'text-red-400'}>
                                {task.auditoryScore?.toFixed(0)}%
                                {task.auditoryResults && task.auditoryStats && (
                                  <span className="text-gray-500 ml-1">
                                    ({task.auditoryResults.correct}/{task.auditoryStats.totalExpected})
                                  </span>
                                )}
                              </span>
                            )
                          }
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className={`font-medium ${task.finalScore >= 70 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {task.finalScore?.toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Deviation Chart - Only for non-real-test mode (real test chart is above the table) */}
          {!isRealTest && (
            <div className="bg-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Deviation Over Time
              </h3>
              {chartData ? (
                <DeviationChart chartData={chartData} height={250} />
              ) : (
                <div className="h-[250px] flex items-center justify-center">
                  <div className="text-gray-500">No deviation data available</div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons Row */}
          <div className="flex gap-4">
            {!isRealTest && (
              <button
                onClick={() => navigate(`${gamesBase}/mic/${subjectId}/play`, { state: location.state })}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                <PlayIcon className="mr-2 h-5 w-5" />
                Play Again
              </button>
            )}
            <button
              onClick={() => navigate(`${gamesBase}/mic/${subjectId}`)}
              className={`${isRealTest ? 'w-full' : 'flex-1'} flex items-center justify-center px-6 py-3 bg-gray-700 text-gray-200 rounded-xl hover:bg-gray-600 transition-colors font-medium`}
            >
              <Cog6ToothIcon className="mr-2 h-5 w-5" />
              Back to Setup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MICResults;
